import { api } from '../utils/api';
import { getPrinterConfig } from '../hooks/usePrinterConfig';
import type {
  OrderItemInput,
  FulfillmentType,
  ServeMode,
  OrderDestination,
} from '../types/@types/order';


export interface CreateOrderPayload {
  table?: string;
  parentTableId?: string;
  orderSequence?: number;
  fulfillment_type?: FulfillmentType;
  meta?: { forTakeaway: boolean };
  destination?: OrderDestination;
  customerName?: string;
  customerPhone?: string;
  pickupEta?: string;
  items: {
    product: string | number;
    quantity: number;
    notes?: string;
    flavors?: { flavorId: string; qty: number }[];
    additions?: Record<string, number>;
    exceptions?: string[];
    fulfillment_type_override?: FulfillmentType;
    serveMode?: ServeMode;
  }[];
}

export async function createOrder(payload: CreateOrderPayload) {
  return api.post('/orders', payload);
}

export interface CreateTakeawayOrderPayload
  extends Omit<CreateOrderPayload, 'table' | 'destination'> {
  customerName: string;
  destination?: 'TAKEAWAY';
}

export async function createTakeawayOrder(payload: CreateTakeawayOrderPayload) {
  const body = {
    ...payload,
    destination: 'TAKEAWAY' as const,
  };
  return api.post('/orders/takeaway', body);
}

export interface GetOrdersParams {
  destination?: OrderDestination;
  q?: string;
  page?: number;
  paid?: boolean;
}

export async function getOrders(params?: GetOrdersParams) {
  const { data } = await api.get('/orders', {
    params: params && Object.keys(params).length > 0 ? params : undefined,
  });
  // Backend now returns paginated { data: Order[], total, ... } — unwrap to array
  return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
}

export async function payOrder(id: string | number) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.patch(`/orders/${id}/pay`);
  return data;
}

export async function getOrder(id?: string | number) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.get(`/orders/${id}`);
  return data;
}

export async function getOrdersByTable(tableId: string | number) {
  if (!tableId) {
    throw new Error('Invalid table id');
  }
  const { data } = await api.get(`/orders/table/${tableId}`);
  return Array.isArray(data) ? data : [];
}

export async function addItemsToOrder(
  id: string | number,
  payload: { items: CreateOrderPayload['items'] },
) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  return api.post(`/orders/${id}/items`, payload);
}

/** Quita un ítem de una orden activa (cliente ya no lo quiere). */
export async function removeOrderItem(orderId: string | number, itemId: string | number) {
  if (!orderId || !itemId) {
    throw new Error('Invalid order or item id');
  }
  const { data } = await api.delete(`/orders/${orderId}/items/${itemId}`);
  return data;
}

export async function updateOrderStatus(
  id: string | number,
  status: 'IN_PRODUCTION' | 'DONE' | 'PAID' | 'DELIVERED',
) {
  if (!id) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.put(`/orders/${id}/status`, { status });
  return data;
}

export async function priceCheck(payload: { items: OrderItemInput[] }) {
  const { data } = await api.post('/orders/price-check', payload);
  return data;
}

export async function updateOrderFulfillment(
  orderId: string | number,
  next: FulfillmentType,
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  return api.patch(`/orders/${orderId}`, {
    fulfillment_type: next,
  });
}

export async function updateItemFulfillmentOverride(
  orderId: string | number,
  itemId: string | number,
  next: FulfillmentType | null,
) {
  if (!orderId || !itemId) {
    throw new Error('Invalid ids');
  }
  return api.patch(`/orders/${orderId}/items/${itemId}`, {
    fulfillment_type_override: next,
  });
}

export async function sendPendingToKitchen(
  orderId: string | number,
  options?: { idempotencyKey?: string },
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }

  const printerConfig = getPrinterConfig();
  const printerIp = typeof window !== 'undefined' ? (window.localStorage.getItem('printer_ip') || '') : '';
  const printerPort = typeof window !== 'undefined' ? (window.localStorage.getItem('printer_port') || '9100') : '9100';

  return api.post(
    `/orders/${orderId}/send-to-kitchen`,
    {
      host: printerIp,
      port: printerPort,
      enabled: Boolean(printerIp),
      paperWidth: printerConfig.paperWidth,
    },
    options?.idempotencyKey
      ? {
        headers: {
          'Idempotency-Key': options.idempotencyKey,
        },
      }
      : undefined,
  );
}
// ── Descuentos ──────────────────────────────────────────────────────────────

export interface ApplyDiscountPayload {
  discountAmount?: number;
  discountPercent?: number;
  discountNote?: string;
}

export interface DiscountResult {
  id: number;
  originalTotal: number;
  discountAmount: number;
  total: number;
  discountNote?: string;
}

export async function applyDiscount(
  orderId: string | number,
  payload: ApplyDiscountPayload,
): Promise<DiscountResult> {
  const { data } = await api.patch(`/orders/${orderId}/discount`, payload);
  return data;
}

export async function removeDiscount(orderId: string | number): Promise<{ id: number; total: number; discountAmount: number }> {
  const { data } = await api.delete(`/orders/${orderId}/discount`);
  return data;
}

export async function splitOrder(
  orderId: string | number,
  itemIds: (string | number)[],
) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  return api.post(`/orders/${orderId}/split`, { item_ids: itemIds });
}

export async function updateOrderTable(orderId: string | number, tableId: string | number) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  const { data } = await api.put(`/orders/${orderId}`, { tableId });
  return data;
}
